import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Input, CustomSelect } from '@redpanda/ui';
import { FormFieldWithError } from './forms';
import { useDialogClickOutside, useModalScrollLock } from '../hooks';
import { toast } from 'react-hot-toast';
import { bankAccountService } from '../services/api';
import { useBankAccountStoreBase } from '../stores/bankAccountStore';

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  currentBalance: number;
  isActive: boolean;
  description?: string;
}

interface BankAccountDialogProps {
  isOpen: boolean;
  onClose: () => void;
  account?: BankAccount | null;
  onAccountCreated?: (account: BankAccount) => void;
  onAccountUpdated?: (account: BankAccount) => void;
}

interface FormData {
  bankName: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  currentBalance: number;
  isActive: boolean;
  description: string;
}

const BankAccountDialog: React.FC<BankAccountDialogProps> = ({
  isOpen,
  onClose,
  account,
  onAccountCreated,
  onAccountUpdated
}) => {
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue, setError } = useForm<FormData>({
    defaultValues: {
      bankName: '',
      accountNumber: '',
      accountName: '',
      accountType: 'CHECKING', // 預設支票帳戶
      currentBalance: 0,
      isActive: true,
      description: ''
    }
  });
  const { t } = useTranslation();

  // 鎖定背景滾動
  useModalScrollLock(isOpen);

  const handleClose = () => {
    reset();
    onClose();
  };

  // 點擊外部關閉 dialog
  const handleBackgroundClick = useDialogClickOutside(handleClose);

  // 當帳戶資料改變時更新表單
  useEffect(() => {
    if (account) {
      reset({
        bankName: account.bankName,
        accountNumber: account.accountNumber,
        accountName: account.accountName,
        accountType: account.accountType,
        currentBalance: account.currentBalance,
        isActive: account.isActive,
        description: account.description || ''
      });
    } else {
      reset({
        bankName: '',
        accountNumber: '',
        accountName: '',
        accountType: 'CHECKING',
        currentBalance: 0,
        isActive: true,
        description: ''
      });
    }
  }, [account, reset]);

  // 處理表單提交
  const onSubmit = async (data: FormData) => {
    if (account) {
      // UPDATE 樂觀更新
      const originalAccount = { ...account };
      const optimisticAccount = { ...account, ...data };

      useBankAccountStoreBase.getState().updateItem(account.id, optimisticAccount);
      handleClose();
      onAccountUpdated?.(optimisticAccount);

      try {
        const response = await bankAccountService.updateBankAccount(account.id, data);
        const realAccount = response.data.data || optimisticAccount;
        useBankAccountStoreBase.getState().updateItem(account.id, realAccount);
        toast.success(t('common.messages.updateSuccess'));
      } catch (error: any) {
        useBankAccountStoreBase.getState().updateItem(account.id, originalAccount);
        const msg = error.response?.data?.message || t('bankAccounts.dialog.updateError');
        toast.error(msg);
      }
    } else {
      // CREATE 樂觀更新
      const tempId = `temp-${Date.now()}`;
      const tempAccount = {
        id: tempId,
        bankName: data.bankName,
        accountNumber: data.accountNumber,
        accountName: data.accountName,
        accountType: data.accountType,
        currentBalance: Number(data.currentBalance),
        isActive: data.isActive,
        description: data.description,
        createdAt: new Date().toISOString(),
      };

      useBankAccountStoreBase.getState().addItem(tempAccount as any);
      handleClose();
      onAccountCreated?.(tempAccount as any);

      try {
        const response = await bankAccountService.createBankAccount(data);
        const realAccount = response.data.data || tempAccount;
        useBankAccountStoreBase.getState().updateItem(tempId, realAccount);
        toast.success(t('common.messages.createSuccess'));
      } catch (error: any) {
        useBankAccountStoreBase.setState((state: any) => ({
          items: state.items.filter((i: any) => i.id !== tempId),
          pagination: state.pagination ? {
            ...state.pagination,
            totalItems: Math.max(0, state.pagination.totalItems - 1),
          } : null,
        }));
        const msg = error.response?.data?.message || t('bankAccounts.dialog.createError');
        toast.error(msg);
      }
    }
  };

  if (!isOpen) return null;


  // 帳戶類型選項（按使用頻率排序）
  const accountTypes = [
    { value: '', label: t('bankAccounts.dialog.selectAccountType') },
    { value: 'CHECKING', label: t('bankAccounts.types.CHECKING') },        // 最常用
    { value: 'SAVINGS', label: t('bankAccounts.types.SAVINGS') },         // 第二常用
    { value: 'CREDIT_LINE', label: t('bankAccounts.types.CREDIT_LINE') },     // 企業常用
    { value: 'FIXED_DEPOSIT', label: t('bankAccounts.types.FIXED_DEPOSIT') },   // 較少用
    { value: 'LOAN', label: t('bankAccounts.types.LOAN') }             // 特殊用途
  ];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto overflow-x-visible"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 對話框標題 */}
        <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {account ? t('bankAccounts.edit') : t('bankAccounts.add')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 對話框內容 */}
        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 overflow-visible">

            {/* Row 1: 銀行名稱 | 帳戶名稱 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormFieldWithError
                label={t('bankAccounts.dialog.bankName')}
                required={true}
                error={errors.bankName?.message}
                htmlFor="bankName"
              >
                <Input
                  id="bankName"
                  type="text"
                  {...register('bankName', { required: t('bankAccounts.dialog.required') })}
                  variant={errors.bankName ? "error" : "default"}
                />
              </FormFieldWithError>

              <FormFieldWithError
                label={t('bankAccounts.dialog.accountName')}
                required={true}
                error={errors.accountName?.message}
                htmlFor="accountName"
              >
                <Input
                  id="accountName"
                  type="text"
                  {...register('accountName', { required: t('bankAccounts.dialog.required') })}
                  variant={errors.accountName ? "error" : "default"}
                />
              </FormFieldWithError>
            </div>

            {/* Row 2: 帳號 | 帳戶類型 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormFieldWithError
                label={t('bankAccounts.dialog.accountNumber')}
                required={true}
                error={errors.accountNumber?.message}
                htmlFor="accountNumber"
              >
                <Input
                  id="accountNumber"
                  type="text"
                  {...register('accountNumber', {
                    required: t('bankAccounts.dialog.required'),
                    pattern: {
                      value: /^[0-9-]+$/,
                      message: t('bankAccounts.dialog.accountNumberPattern')
                    }
                  })}
                  variant={errors.accountNumber ? "error" : "default"}
                />
              </FormFieldWithError>

              <FormFieldWithError
                label={t('bankAccounts.dialog.accountType')}
                required={true}
                error={errors.accountType?.message}
                htmlFor="accountType"
              >
                <CustomSelect
                  id="accountType"
                  {...register('accountType', { required: t('bankAccounts.dialog.required') })}
                  value={watch('accountType') || ''}
                  onChange={(value) => setValue('accountType', value)}
                  variant={errors.accountType ? "error" : "default"}
                  options={accountTypes}
                />
              </FormFieldWithError>
            </div>

            {/* Row 3: 目前餘額 | 備註 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormFieldWithError
                label={t('bankAccounts.dialog.currentBalance')}
                required={true}
                error={errors.currentBalance?.message}
                htmlFor="currentBalance"
              >
                <Input
                  id="currentBalance"
                  type="text"
                  {...register('currentBalance', {
                    required: t('bankAccounts.dialog.required'),
                    setValueAs: (value) => {
                      const num = parseFloat(value);
                      return isNaN(num) ? 0 : num;
                    },
                    min: {
                      value: 0,
                      message: t('bankAccounts.dialog.balanceNotNegative')
                    }
                  })}
                  variant={errors.currentBalance ? "error" : "default"}
                />
              </FormFieldWithError>

              <FormFieldWithError
                label={t('bankAccounts.dialog.description')}
                required={false}
                error={errors.description?.message}
                htmlFor="description"
              >
                <Input
                  id="description"
                  type="text"
                  {...register('description')}
                  variant={errors.description ? "error" : "default"}
                />
              </FormFieldWithError>
            </div>

            {/* 帳戶狀態 */}
            <div className="flex items-center justify-between">
              <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('bankAccounts.dialog.accountStatus')}
              </label>
              <div className="flex items-center space-x-2">
                <span className={`text-sm ${watch('isActive') ? 'text-gray-500' : 'text-gray-900 dark:text-white font-medium'}`}>
                  {t('common.status.inactive')}
                </span>
                <button
                  type="button"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 ${
                    watch('isActive') ? 'bg-brand-600' : 'bg-gray-200'
                  }`}
                  onClick={() => setValue('isActive', !watch('isActive'))}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      watch('isActive') ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className={`text-sm ${watch('isActive') ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500'}`}>
                  {t('common.status.active')}
                </span>
              </div>
            </div>

            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse pt-6 border-t border-gray-200 dark:border-slate-700">
              <button
                type="submit"
                className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
              >
                {account ? t('common.actions.update') : t('common.actions.add')}
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md"
                onClick={handleClose}
              >
                {t('common.actions.cancel')}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default BankAccountDialog;